import React, { useState } from 'react';
import { Plus, Search, Mail, Phone, Edit2, Trash2, X, Lock, Eye, Upload } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { uploadStorageImage } from '../../services/websiteAssets';
import {
  provisionEmployeePortal,
  deprovisionEmployeePortal,
  formatProvisionResultMessage,
} from '../../services/backendActions';
import './AdminPages.css';

const ROLE_COLORS = { super_admin:'#C9A227', admin:'#3182ce', hr:'#38a169', mentor:'#805ad5', finance:'#d69e2e', employee:'#e53e3e' };
const ROLE_LABELS = { super_admin:'Founder', admin:'Admin', hr:'HR', mentor:'Mentor', finance:'Finance', employee:'Employee' };

const emptyForm = { name:'', email:'', phone:'', title:'', dept:'Development', role:'employee', salary:'', status:'Active', photoUrl:'' };

export default function EmployeesPage() {
  const { data, commitData, showToast } = useApp();
  const { can, isFounder } = useAuth();

  const [search, setSearch]       = useState('');
  const [deptFilter, setDeptFilter] = useState('All');
  const [showAdd, setShowAdd]     = useState(false);
  const [showEdit, setShowEdit]   = useState(false);
  const [showView, setShowView]   = useState(false);
  const [selected, setSelected]   = useState(null);
  const [form, setForm]           = useState(emptyForm);
  const [resendingPortal, setResendingPortal] = useState(false);

  const canAdd    = can('canAddEmployee');
  const canEdit   = can('canEditEmployee');
  const canDelete = can('canDeleteEmployee');

  const depts = ['All', ...new Set(data.employees.map(e => e.dept))];

  const filtered = data.employees.filter(e => {
    const s = e.name.toLowerCase().includes(search.toLowerCase()) || e.title?.toLowerCase().includes(search.toLowerCase()) || e.email.toLowerCase().includes(search.toLowerCase());
    const d = deptFilter === 'All' || e.dept === deptFilter;
    return s && d;
  });

  const portalSetupMessage = (err, fallback) => {
    const code = err?.code || '';
    const msg = err?.message || fallback;
    if (code === 'functions/not-found' || msg.includes('not-found')) {
      return 'Employee saved. Deploy Cloud Functions (provisionEmployeeUser) to enable automatic portal setup.';
    }
    return msg;
  };

  const runPortalProvision = async (employeeId) => {
    const result = await provisionEmployeePortal(employeeId);
    const type = result?.emailSent ? 'success' : result?.tempPassword ? 'warning' : 'success';
    showToast(formatProvisionResultMessage(result), type);
    return result;
  };

  const handleAdd = async () => {
    if (!form.name || !form.email) { showToast('Name and email are required', 'error'); return; }
    const initials = form.name.split(' ').slice(0,2).map(n=>n[0]).join('').toUpperCase();
    const emp = { id: String(Date.now()), ...form, avatar: initials, joined: new Date().toISOString().split('T')[0], salary: parseFloat(form.salary)||0 };
    try {
      await commitData('employees', [...data.employees, emp]);
      try {
        await runPortalProvision(emp.id);
      } catch (provErr) {
        showToast(portalSetupMessage(provErr, 'Employee saved but portal setup failed.'), 'warning');
      }
      setShowAdd(false);
      setForm(emptyForm);
    } catch {}
  };

  const openEdit = (emp) => {
    setSelected(emp);
    setForm({ ...emp, salary: emp.salary?.toString() || '' });
    setShowEdit(true);
  };

  const handleEdit = async () => {
    const prev = selected;
    const next = { ...prev, ...form, salary: parseFloat(form.salary) || 0 };
    const profileChanged =
      prev.email !== next.email ||
      prev.role !== next.role ||
      prev.name !== next.name ||
      prev.dept !== next.dept ||
      prev.title !== next.title;
    try {
      await commitData('employees', data.employees.map((e) => (e.id === selected.id ? next : e)));
      if (profileChanged) {
        try {
          await runPortalProvision(selected.id);
        } catch (provErr) {
          showToast(portalSetupMessage(provErr, 'Employee updated but portal sync failed.'), 'warning');
        }
      } else {
        showToast('Employee updated');
      }
      setShowEdit(false);
      setSelected(null);
    } catch {}
  };

  const handleDelete = async (emp) => {
    if (emp.role === 'super_admin') { showToast('Cannot delete a founder account', 'error'); return; }
    if (!window.confirm(`Delete ${emp.name}? Their portal login will be removed. This cannot be undone.`)) return;
    try {
      if (emp.authUid) {
        try {
          await deprovisionEmployeePortal(emp.id);
        } catch (depErr) {
          showToast(depErr?.message || 'Could not remove portal login', 'warning');
        }
      }
      await commitData('employees', data.employees.filter((e) => e.id !== emp.id));
      showToast(`${emp.name} removed`, 'warning');
    } catch {}
  };

  const toggleStatus = async (emp) => {
    const newStatus = emp.status === 'Active' ? 'Inactive' : 'Active';
    try {
      await commitData('employees', data.employees.map(e => e.id === emp.id ? { ...e, status: newStatus } : e));
      showToast(`${emp.name} set to ${newStatus}`);
    } catch {}
  };

  const uploadPhoto = async (target, file) => {
    if (!file) return;
    try {
      const url = await uploadStorageImage(`employeeProfiles/${target.id}/photo`, file);
      await commitData('employees', data.employees.map((e) => (e.id === target.id ? { ...e, photoUrl: url } : e)));
      showToast('Profile picture updated');
      if (selected?.id === target.id) setSelected((prev) => ({ ...prev, photoUrl: url }));
    } catch (err) {
      showToast(err.message || 'Upload failed', 'error');
    }
  };

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Employees</h1>
          <p className="page-subtitle">{data.employees.length} team members · {data.employees.filter(e=>e.status==='Active').length} active</p>
        </div>
        <div className="page-actions">
          {canAdd && <button className="btn-gold" onClick={()=>{setForm(emptyForm);setShowAdd(true)}}><Plus size={15}/> Add Employee</button>}
          {!canAdd && <span className="badge badge-gray"><Lock size={10}/> View Only</span>}
        </div>
      </div>

      <div className="filter-bar">
        <div className="search-box">
          <Search size={15}/>
          <input className="search-input" placeholder="Search employees..." value={search} onChange={e=>setSearch(e.target.value)}/>
        </div>
        <div className="filter-tabs">
          {depts.map(d=><button key={d} className={`filter-tab ${deptFilter===d?'active':''}`} onClick={()=>setDeptFilter(d)}>{d}</button>)}
        </div>
      </div>

      <div className="cards-grid">
        {filtered.map(emp => {
          const rc = ROLE_COLORS[emp.role] || '#888';
          return (
            <div key={emp.id} className="emp-card">
              <div className="emp-avatar" style={{ background:`linear-gradient(135deg,${rc},${rc}88)`, color: emp.role==='super_admin'?'#000':'#fff' }}>
                {emp.photoUrl ? <img src={emp.photoUrl} alt="" style={{ width:'100%',height:'100%',objectFit:'cover',borderRadius:'50%' }} /> : emp.avatar}
              </div>
              <p className="emp-name">{emp.name}</p>
              <p className="emp-title">{emp.title}</p>
              <p className="emp-dept">{emp.dept}</p>
              <div style={{ display:'flex',gap:6,alignItems:'center' }}>
                <span className={`badge ${emp.status==='Active'?'badge-success':emp.status==='On Leave'?'badge-warning':'badge-gray'}`}>{emp.status}</span>
                <span className="badge" style={{ background:`${rc}15`,color:rc,border:`1px solid ${rc}40` }}>{ROLE_LABELS[emp.role]||emp.role}</span>
              </div>
              <div className="emp-card-divider"/>
              <div style={{ fontSize:'0.75rem',color:'var(--text-muted)',display:'flex',flexDirection:'column',gap:4,width:'100%',textAlign:'left' }}>
                <div style={{ display:'flex',alignItems:'center',gap:6 }}><Mail size={11}/>{emp.email}</div>
                {emp.phone && <div style={{ display:'flex',alignItems:'center',gap:6 }}><Phone size={11}/>{emp.phone}</div>}
                <div>Joined: {new Date(emp.joined).toLocaleDateString('en-ZA',{month:'short',year:'numeric'})}</div>
                {isFounder && emp.salary > 0 && <div style={{ color:'var(--secondary-color)',fontWeight:700 }}>R {emp.salary.toLocaleString()}/mo</div>}
              </div>
              <div className="emp-card-actions">
                <button className="btn-ghost" onClick={()=>{setSelected(emp);setShowView(true)}}><Eye size={12}/> View</button>
                {canEdit && (
                  <label className="btn-ghost" style={{ cursor:'pointer', display:'inline-flex', alignItems:'center', gap:4 }}>
                    <Upload size={12}/> Photo
                    <input type="file" accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml" style={{ display:'none' }} onChange={(e)=>{ const f=e.target.files?.[0]; e.target.value=''; uploadPhoto(emp, f); }} />
                  </label>
                )}
                {canEdit && <button className="btn-ghost" onClick={()=>openEdit(emp)}><Edit2 size={12}/> Edit</button>}
                {isFounder && emp.role!=='super_admin' && (
                  <button className="btn-ghost" style={{ color:'var(--danger)' }} onClick={()=>handleDelete(emp)}><Trash2 size={12}/></button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ─── VIEW MODAL ─── */}
      {showView && selected && (
        <div className="modal-overlay" onClick={()=>setShowView(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <p className="modal-title">{selected.name}</p>
              <button className="modal-close" onClick={()=>setShowView(false)}><X size={18}/></button>
            </div>
            <div style={{ display:'flex',flexDirection:'column',alignItems:'center',gap:8,padding:'12px 0 20px',borderBottom:'1px solid var(--border-color)',marginBottom:20 }}>
              <div className="emp-avatar" style={{ width:64,height:64,background:`linear-gradient(135deg,${ROLE_COLORS[selected.role]||'#888'},${ROLE_COLORS[selected.role]||'#888'}88)`,color:selected.role==='super_admin'?'#000':'#fff',fontSize:'1.2rem',overflow:'hidden' }}>{selected.photoUrl ? <img src={selected.photoUrl} alt="" style={{ width:'100%',height:'100%',objectFit:'cover' }} /> : selected.avatar}</div>
              <p style={{ fontWeight:700,fontSize:'1.05rem' }}>{selected.name}</p>
              <p style={{ fontSize:'0.82rem',color:'var(--secondary-color)' }}>{selected.title}</p>
              <span className={`badge ${selected.status==='Active'?'badge-success':'badge-gray'}`}>{selected.status}</span>
            </div>
            <div className="detail-grid">
              <div className="detail-item"><span className="detail-label">Department</span><span className="detail-value">{selected.dept}</span></div>
              <div className="detail-item"><span className="detail-label">Role</span><span className="detail-value">{ROLE_LABELS[selected.role]}</span></div>
              <div className="detail-item"><span className="detail-label">Email</span><span className="detail-value" style={{ fontSize:'0.8rem' }}>{selected.email}</span></div>
              <div className="detail-item"><span className="detail-label">Phone</span><span className="detail-value">{selected.phone||'—'}</span></div>
              <div className="detail-item"><span className="detail-label">Joined</span><span className="detail-value">{new Date(selected.joined).toLocaleDateString('en-ZA')}</span></div>
              {isFounder && <div className="detail-item"><span className="detail-label">Salary</span><span className="detail-value" style={{ color:'var(--secondary-color)' }}>R {selected.salary?.toLocaleString()}/mo</span></div>}
            </div>
            {/* Assigned projects */}
            <p style={{ fontSize:'0.72rem',fontWeight:700,color:'var(--text-dim)',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:8 }}>Assigned Projects</p>
            {data.projects.filter(p=>(p.team||[]).map(String).includes(String(selected.id))).map(p=>(
              <div key={p.id} style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'7px 0',borderBottom:'1px solid var(--border-color)',fontSize:'0.82rem' }}>
                <span style={{ fontWeight:600 }}>{p.name}</span>
                <span className={`badge ${STATUS_BADGE[p.status]||'badge-gray'}`}>{p.status}</span>
              </div>
            ))}
            {data.projects.filter(p=>(p.team||[]).map(String).includes(String(selected.id))).length===0 && <p style={{ fontSize:'0.8rem',color:'var(--text-dim)' }}>No projects assigned</p>}
            <div style={{ display:'flex',gap:10,justifyContent:'flex-end',marginTop:20,flexWrap:'wrap' }}>
              {canEdit && selected.role!=='super_admin' && (
                <button
                  type="button"
                  className="btn-outline"
                  disabled={resendingPortal}
                  onClick={async () => {
                    setResendingPortal(true);
                    try {
                      const result = await provisionEmployeePortal(selected.id, { resendWelcome: true });
                      showToast(formatProvisionResultMessage(result), result?.emailSent ? 'success' : 'warning');
                    } catch (e) {
                      showToast(portalSetupMessage(e, 'Could not resend portal invite.'), 'error');
                    }
                    setResendingPortal(false);
                  }}
                >
                  {resendingPortal ? 'Sending…' : 'Resend portal invite'}
                </button>
              )}
              {canEdit && selected.role!=='super_admin' && (
                <button className="btn-gold" onClick={()=>{setShowView(false);openEdit(selected)}}>Edit Employee</button>
              )}
              {isFounder && selected.role!=='super_admin' && (
                <button className="btn-ghost" style={{ color:'var(--warning)' }} onClick={()=>{toggleStatus(selected);setShowView(false)}}>
                  {selected.status==='Active'?'Deactivate':'Activate'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── ADD / EDIT MODAL ─── */}
      {(showAdd||showEdit) && (
        <div className="modal-overlay" onClick={()=>{setShowAdd(false);setShowEdit(false)}}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <p className="modal-title">{showAdd?'Add Employee':'Edit Employee'}</p>
              <button className="modal-close" onClick={()=>{setShowAdd(false);setShowEdit(false)}}><X size={18}/></button>
            </div>
            <div className="modal-form">
              <div className="form-row">
                <div className="form-group"><label className="form-label">Full Name *</label><input className="form-input" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="Full name"/></div>
                <div className="form-group"><label className="form-label">Email *</label><input className="form-input" type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} placeholder="name@fitss.co.za"/></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Job Title</label><input className="form-input" value={form.title||''} onChange={e=>setForm({...form,title:e.target.value})} placeholder="E.g. Frontend Developer"/></div>
                <div className="form-group"><label className="form-label">Phone</label><input className="form-input" value={form.phone||''} onChange={e=>setForm({...form,phone:e.target.value})} placeholder="+27 82 000 0000"/></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Department</label>
                  <select className="form-input" value={form.dept} onChange={e=>setForm({...form,dept:e.target.value})}>
                    {['Development','Design','Systems','HR','Finance','Operations','Executive'].map(d=><option key={d}>{d}</option>)}
                  </select>
                </div>
                <div className="form-group"><label className="form-label">Role / Access Level</label>
                  <select className="form-input" value={form.role} onChange={e=>setForm({...form,role:e.target.value})} disabled={!isFounder}>
                    <option value="employee">Employee</option>
                    <option value="mentor">Mentor</option>
                    <option value="hr">HR Manager</option>
                    <option value="finance">Finance Dept</option>
                    <option value="admin">Administrator</option>
                    {isFounder && <option value="super_admin">Founder (Super Admin)</option>}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Status</label>
                  <select className="form-input" value={form.status} onChange={e=>setForm({...form,status:e.target.value})}>
                    {['Active','On Leave','Inactive'].map(s=><option key={s}>{s}</option>)}
                  </select>
                </div>
                {isFounder && <div className="form-group"><label className="form-label">Monthly Salary (R)</label><input className="form-input" type="number" value={form.salary||''} onChange={e=>setForm({...form,salary:e.target.value})} placeholder="0.00"/></div>}
              </div>
              <div className="form-group">
                <label className="form-label">Profile Picture URL</label>
                <input className="form-input" value={form.photoUrl||''} onChange={e=>setForm({...form,photoUrl:e.target.value})} placeholder="https://..." />
              </div>
              {showAdd && (
                <p style={{ fontSize:'0.78rem',color:'var(--text-muted)',lineHeight:1.55,marginTop:4 }}>
                  Saving will create a Firebase login, a user profile, and send a welcome email when SMTP is configured on Cloud Functions.
                </p>
              )}
            </div>
            <div className="modal-actions">
              <button className="btn-ghost" onClick={()=>{setShowAdd(false);setShowEdit(false)}}>Cancel</button>
              <button className="btn-gold" onClick={showAdd?handleAdd:handleEdit}>{showAdd?'Add Employee':'Save Changes'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const STATUS_BADGE = { Planning:'badge-gray','In Progress':'badge-info',Review:'badge-warning',Completed:'badge-success' };
